import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ChevronLeft,
  ArrowLeft,
  Copy,
  CheckCircle,
  ExternalLink,
  Send,
  XCircle,
  Loader2,
  Clock,
  AlertCircle,
  CreditCard,
  Hash,
  Search,
  MoreHorizontal,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppKit, useAppKitAccount, useAppKitNetwork } from '@reown/appkit/react';
import { useWriteContract, useSwitchChain, useSendTransaction } from 'wagmi';
import { parseUnits } from 'viem';

import '../styles/responsive/pages/PayoutDetail.css';
import '../styles/enterprise.css';
import { fetchWithAuth } from '../utils/api';
import { formatUSD, formatCrypto, truncateHash } from '../utils/formatAmount';
import StatusBadge from '../components/common/StatusBadge';
import { TokenChainLogo } from '../components/common/CryptoLogo';
import Skeleton from '../components/common/Skeleton';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Read a field the API may expose as camelCase or snake_case (mirrors the list pages).
const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return undefined;
};

// An admin rejection is not a failed blockchain transaction, so 'rejected' keeps
// its own red "Rejected" pill. 'failed' stays reserved for on-chain failures.

const PayoutDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.role_id === 1 || user?.roleId === 1;

  // Admin row -> /dashboard/payout-requests; merchant row -> /dashboard/payouts.
  const backTo = isSuperAdmin ? '/dashboard/payout-requests' : '/dashboard/payouts';
  const backLabel = isSuperAdmin ? 'Payout requests' : 'Payouts';

  // Wallet hooks (admin on-chain Approve & Pay flow — preserved verbatim).
  const { open: openWalletModal } = useAppKit();
  const { address: walletAddress, isConnected } = useAppKitAccount();
  const { chainId: currentChainId } = useAppKitNetwork();
  const { switchChainAsync } = useSwitchChain();
  const { sendTransactionAsync } = useSendTransaction();
  const { writeContractAsync } = useWriteContract();

  // Track live chain id via ref so async callbacks read the current value, not a stale closure.
  const liveChainIdRef = useRef(currentChainId);
  useEffect(() => { liveChainIdRef.current = currentChainId; }, [currentChainId]);

  // Seed from the row the list already loaded for an instant first paint, then refetch.
  const [payout, setPayout] = useState(location.state?.request || null);
  const [loading, setLoading] = useState(!location.state?.request);
  const [notFound, setNotFound] = useState(false);

  const [copiedAddr, setCopiedAddr] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [payoutStep, setPayoutStep] = useState('');

  // Approve manual (paste a tx hash) + Recheck (scan destination for the payout).
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualTxHash, setManualTxHash] = useState('');
  const [manualFromAddress, setManualFromAddress] = useState('');
  const [showRecheckModal, setShowRecheckModal] = useState(false);
  const [recheckLoading, setRecheckLoading] = useState(false);
  const [recheckResult, setRecheckResult] = useState(null); // {scanAvailable, candidates, reason, expected}
  const [mappingHash, setMappingHash] = useState(''); // candidate hash currently being mapped
  const [showActionsMenu, setShowActionsMenu] = useState(false); // header ⋯ dropdown
  // Live confirmation meter while a tx is confirming on-chain.
  const [onchain, setOnchain] = useState(null); // {confirmations, required, confirmed, status}

  const fetchPayout = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/${id}`);
      const data = await response.json();
      if (data.success && data.data) {
        setPayout(data.data);
        setNotFound(false);
      } else {
        setNotFound(true);
      }
    } catch (error) {
      console.error('Failed to fetch payout:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPayout();
  }, [fetchPayout]);

  // ---- Derived figures (same derivation the list pages use) ----
  const rowFee = (request) => {
    const fee = pick(request, 'feeAmount', 'fee_amount');
    return fee !== undefined ? Number(fee) : null;
  };
  const rowNet = (request) => {
    // The native token value is the source of truth: derive net USD from
    // token_amount * conversion_rate when both are present, else fall back to the
    // stored USD columns, else gross minus fee.
    const tokenAmount = pick(request, 'tokenAmount', 'token_amount');
    const rate = pick(request, 'conversionRate', 'conversion_rate');
    if (tokenAmount != null && rate != null && Number(rate) > 0) {
      return Number(tokenAmount) * Number(rate);
    }
    const net = pick(request, 'netAmount', 'net_amount');
    if (net !== undefined) return Number(net);
    const fee = rowFee(request);
    return fee != null ? Number(request.amount) - fee : null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCopyAddress = () => {
    if (!payout?.destinationAddress) return;
    navigator.clipboard.writeText(payout.destinationAddress);
    setCopiedAddr(true);
    toast.success('Address copied');
    setTimeout(() => setCopiedAddr(false), 1600);
  };

  const checkPayoutStatus = async (requestId) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/${requestId}`);
      const data = await response.json();
      if (data.success && data.data) return data.data;
      return null;
    } catch (error) {
      console.error('Failed to check payout status:', error);
      return null;
    }
  };

  // Verify a tx hash on-chain and mark the payout paid. Shared by "Approve manual"
  // (pasted hash) and "Recheck → map" (selected candidate).
  const verifyAndComplete = useCallback(async (txHash, fromAddr) => {
    const res = await fetchWithAuth(`${API_BASE_URL}/payouts/admin/${id}/verify-complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ txHash, fromAddress: fromAddr || undefined }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Could not verify this transaction');
    }
    return data.data; // {status, txHash, confirmations, required}
  }, [id]);

  const handleApproveManual = async () => {
    const hash = manualTxHash.trim();
    if (!/^0x[a-fA-F0-9]{64}$/.test(hash)) {
      toast.error('Enter a valid transaction hash (0x + 64 hex characters)');
      return;
    }
    setActionLoading(true);
    try {
      const result = await verifyAndComplete(hash, manualFromAddress.trim());
      toast.success(result.status === 'completed' ? 'Verified — payout marked paid' : 'Transaction found — confirming on-chain');
      setShowManualModal(false);
      setManualTxHash('');
      setManualFromAddress('');
      await fetchPayout();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRecheck = async () => {
    setRecheckLoading(true);
    setShowRecheckModal(true);
    setRecheckResult(null);
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/payouts/admin/${id}/recheck`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setRecheckResult(data.data);
      } else {
        setRecheckResult({ scanAvailable: false, candidates: [], reason: data.message || 'RECHECK_FAILED' });
      }
    } catch (err) {
      setRecheckResult({ scanAvailable: false, candidates: [], reason: 'RECHECK_FAILED' });
    } finally {
      setRecheckLoading(false);
    }
  };

  const handleMapCandidate = async (hash) => {
    setMappingHash(hash);
    try {
      const result = await verifyAndComplete(hash);
      toast.success(result.status === 'completed' ? 'Mapped — payout marked paid' : 'Mapped — confirming on-chain');
      setShowRecheckModal(false);
      await fetchPayout();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setMappingHash('');
    }
  };

  // While a payout is confirming, poll on-chain status every 60s for the meter,
  // and refetch the full record once it confirms so the status flips to completed.
  useEffect(() => {
    if (payout?.status !== 'processing' || !payout?.txHash) {
      setOnchain(null);
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetchWithAuth(`${API_BASE_URL}/payouts/${id}/onchain-status`);
        const data = await res.json();
        if (cancelled || !data.success) return;
        setOnchain(data.data);
        if (data.data.status === 'completed' || data.data.confirmed) {
          fetchPayout();
        }
      } catch (err) {
        /* transient — keep polling */
      }
    };
    poll();
    const timer = setInterval(poll, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [payout?.status, payout?.txHash, id, fetchPayout]);

  // ---- Approve & Pay (admin on-chain wallet flow, preserved from PayoutRequests) ----
  const handleApproveAndPay = async () => {
    if (!payout) return;

    if (!payout.network?.chainId || !payout.destinationAddress || !payout.tokenAmount) {
      toast.error('Missing required transaction data');
      return;
    }

    if (!isConnected) {
      openWalletModal();
      return;
    }

    setActionLoading(true);
    setPayoutStep('preparing');

    try {
      const latestStatus = await checkPayoutStatus(payout.requestId);
      if (latestStatus) {
        if (latestStatus.status !== 'pending') {
          toast.error(`This payout is already ${latestStatus.status}. Please refresh the page.`);
          setActionLoading(false);
          setPayoutStep('');
          fetchPayout();
          return;
        }
        if (latestStatus.txHash) {
          toast.error('A transaction has already been submitted for this payout. Please refresh the page.');
          setActionLoading(false);
          setPayoutStep('');
          fetchPayout();
          return;
        }
      }
    } catch (error) {
      console.error('Failed to verify payout status:', error);
    }

    // Ask the backend whether it will accept this approval BEFORE signing. The
    // wallet broadcast is irreversible, so discovering a refusal afterwards means
    // funds have left the operator wallet with nothing recording them — that is
    // how "Transaction broadcast — could not record approval" happened with the
    // money already debited.
    try {
      const preflightRes = await fetchWithAuth(
        `${API_BASE_URL}/payouts/admin/${payout.requestId}/preflight`
      );
      const preflight = await preflightRes.json();

      if (!preflight.authorized) {
        toast.error(preflight.message || 'This payout cannot be released right now.');
        setActionLoading(false);
        setPayoutStep('');
        fetchPayout();
        return;
      }
    } catch (error) {
      console.error('Payout preflight failed:', error);
      toast.error('Could not verify the payout is releasable — not signing. Please retry.');
      setActionLoading(false);
      setPayoutStep('');
      return;
    }

    setPayoutStep('switching');

    try {
      const requiredChainId = parseInt(payout.network.chainId);
      // Trust the awaited switch result as the source of truth for the live chain.
      // `switchChainAsync` actually waits for the wallet to switch and resolves with
      // the new chain; the previous `switchChain` was fire-and-forget, so awaiting it
      // resolved immediately and the success toast fired even when no switch occurred.
      let liveChainId = liveChainIdRef.current;
      if (liveChainId !== requiredChainId) {
        try {
          const newChain = await switchChainAsync({ chainId: requiredChainId });
          liveChainId = newChain?.id ?? liveChainIdRef.current;
          toast.success(`Switched to ${payout.networkName}`);
        } catch (switchError) {
          console.error('Failed to switch network:', switchError);
          toast.error('Failed to switch network. Please switch manually in your wallet.');
          setPayoutStep('');
          setActionLoading(false);
          return;
        }
      }

      // Hard gate: never sign unless the wallet is verifiably on the required chain.
      // Signing on the wrong chain broadcasts the payout to a chain the backend
      // monitor never watches, leaving the request stuck in "processing" forever.
      if (liveChainId !== requiredChainId) {
        toast.error(`Wallet is on the wrong network — switch to ${payout.networkName} and retry`);
        setPayoutStep('');
        setActionLoading(false);
        return;
      }

      setPayoutStep('preparing');

      const tokenDecimals = payout.token?.decimals || 18;
      let txHash;

      if (payout.token?.isNative || !payout.token?.contractAddress) {
        setPayoutStep('signing');
        toast.loading('Please sign the transaction in your wallet...', { id: 'sign-tx' });

        const value = parseUnits(payout.tokenAmount.toString(), tokenDecimals);

        let timeoutId;
        const signingTimeout = new Promise((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Wallet signing timed out — please try again')), 60000);
        });
        try {
          txHash = await Promise.race([sendTransactionAsync({
            to: payout.destinationAddress,
            value: value,
          }), signingTimeout]);
        } finally {
          clearTimeout(timeoutId);
        }

        toast.dismiss('sign-tx');
      } else {
        setPayoutStep('signing');
        toast.loading('Please sign the transaction in your wallet...', { id: 'sign-tx' });

        const erc20Abi = [
          {
            name: 'transfer',
            type: 'function',
            inputs: [
              { name: 'to', type: 'address' },
              { name: 'amount', type: 'uint256' },
            ],
            outputs: [{ name: '', type: 'bool' }],
          },
        ];

        const amount = parseUnits(payout.tokenAmount.toString(), tokenDecimals);

        let timeoutId;
        const signingTimeout = new Promise((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Wallet signing timed out — please try again')), 60000);
        });
        try {
          txHash = await Promise.race([writeContractAsync({
            address: payout.token.contractAddress,
            abi: erc20Abi,
            functionName: 'transfer',
            args: [payout.destinationAddress, amount],
          }), signingTimeout]);
        } finally {
          clearTimeout(timeoutId);
        }

        toast.dismiss('sign-tx');
      }

      setPayoutStep('completing');

      const approveResponse = await fetchWithAuth(`${API_BASE_URL}/payouts/admin/${payout.requestId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          txHash: txHash,
          adminNotes: 'Payout processed via AppKit',
          fromAddress: walletAddress,
        }),
      });

      const approveData = await approveResponse.json();

      // The old 'pending_second_approval' outcome is gone: approval is
      // single-admin now, so a successful approve always releases the payout.
      if (approveData.success) {
        toast.success('Transaction submitted successfully');
        navigate(backTo);
      } else {
        console.error('Failed to record approval:', approveData.message);
        toast.error('Transaction broadcast — could not record approval, contact ops');
        fetchPayout();
      }
    } catch (error) {
      console.error('Payout failed:', error);
      toast.dismiss('sign-tx');

      if (error.message?.includes('user rejected') || error.message?.includes('User rejected')) {
        toast.error('Transaction cancelled by user');
      } else if (error.message?.includes('insufficient funds')) {
        toast.error('Insufficient funds in wallet');
      } else if (error.message?.includes('PAYOUT_ALREADY_PROCESSED') || error.message?.includes('already been processed')) {
        toast.error('This payout has already been processed. Please refresh the page.');
        fetchPayout();
      } else {
        toast.error(`Payout failed: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setActionLoading(false);
      setPayoutStep('');
    }
  };

  // ---- Reject (admin, reason required) ----
  const handleReject = async () => {
    if (!payout || !rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/admin/${payout.requestId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectionReason.trim() }),
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Payout request rejected');
        setShowRejectModal(false);
        setRejectionReason('');
        fetchPayout();
      } else {
        toast.error(data.message || 'Failed to reject request');
      }
    } catch (error) {
      console.error('Reject failed:', error);
      toast.error('Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  // ---- Cancel (merchant) ----
  const handleCancel = async () => {
    if (!payout) return;
    setActionLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/${payout.requestId}/cancel`, {
        method: 'POST',
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Payout request cancelled');
        navigate(backTo);
      } else {
        toast.error(data.message || 'Failed to cancel request');
      }
    } catch (error) {
      console.error('Failed to cancel request:', error);
      toast.error('Failed to cancel request');
    } finally {
      setActionLoading(false);
    }
  };

  // Lifecycle events, newest first (built from whichever timestamps are present).
  const buildEvents = (p) => {
    const events = [];
    if (p.completedAt) {
      events.push({ type: 'completed', message: 'Payout completed', date: p.completedAt });
    }
    const processingStarted = pick(p, 'processingStartedAt', 'processing_started_at');
    if (processingStarted) {
      events.push({ type: 'processing', message: 'Payout broadcast on-chain', date: processingStarted });
    }
    if (p.status === 'rejected') {
      events.push({ type: 'rejected', message: 'Payout request rejected', date: p.reviewedAt });
    } else if (p.reviewedAt) {
      events.push({ type: 'approved', message: 'Payout request approved', date: p.reviewedAt });
    }
    const heldReason = pick(p, 'heldReason', 'held_reason');
    if (heldReason) {
      events.push({ type: 'held', message: 'Payout placed on hold', date: pick(p, 'heldAt', 'held_at') });
    }
    events.push({ type: 'requested', message: 'Payout requested', date: p.createdAt });
    return events.filter((e) => e.date);
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'completed':
        return (
          <div className="activity-icon-wrapper success">
            <CheckCircle size={14} className="event-icon success" />
          </div>
        );
      case 'processing':
        return (
          <div className="activity-icon-wrapper info">
            <Send size={14} className="event-icon info" />
          </div>
        );
      case 'approved':
        return (
          <div className="activity-icon-wrapper success">
            <CheckCircle size={14} className="event-icon success" />
          </div>
        );
      case 'rejected':
        return (
          <div className="activity-icon-wrapper danger">
            <XCircle size={14} className="event-icon danger" />
          </div>
        );
      case 'held':
        return (
          <div className="activity-icon-wrapper warning">
            <AlertCircle size={14} className="event-icon warning" />
          </div>
        );
      default:
        return (
          <div className="activity-icon-wrapper primary">
            <Clock size={14} className="event-icon primary" />
          </div>
        );
    }
  };

  // Merchant balance card. The settled figure and its deductions are only worth
  // rendering when something is actually deducted — with nothing reserved,
  // `settledBalance` equals `available` and the extra rows just repeat it.
  const balanceHasAdjustments =
    !!payout?.merchantBalance &&
    (payout.merchantBalance.otherPendingPayouts > 0 || payout.merchantBalance.reservedRefundAmount > 0);

  // Where "payout history" goes for each role. The admin queue accepts
  // ?merchantId=<id> and filters to that merchant; a merchant's own list needs
  // no filter. Null when an admin has no merchant on the payload, rather than
  // linking to an unfiltered queue that looks like it failed to filter.
  const balanceHistoryTo = isSuperAdmin
    ? payout?.merchant?.id
      ? `/dashboard/payout-requests?merchantId=${payout.merchant.id}`
      : null
    : '/dashboard/payouts';

  if (loading) {
    return (
      <div className="ent ent-page payout-detail-page">
        <div className="payout-breadcrumb-bar">
          <Skeleton variant="text" width={180} />
        </div>

        <div className="ent-page-head">
          <div>
            <div className="po-head-title">
              <Skeleton variant="text" width={200} height={26} />
            </div>
            <div className="po-head-sub">
              <Skeleton variant="text" width={260} />
            </div>
          </div>
          <div className="ent-page-head__actions">
            <Skeleton variant="line" width={120} height={36} />
            <Skeleton variant="line" width={120} height={36} />
          </div>
        </div>

        <div className="payout-layout">
          <div className="payout-main-content">
            {/* Amount */}
            <div className="payout-card">
              <Skeleton variant="text" width={100} />
              <div className="amount-display">
                <Skeleton variant="line" width={180} height={40} />
                <Skeleton variant="text" width={120} />
              </div>
            </div>

            {/* Summary */}
            <div className="payout-card">
              <Skeleton variant="text" width={100} />
              <Skeleton variant="line" count={3} />
            </div>

            {/* Breakdown */}
            <div className="payout-card">
              <Skeleton variant="text" width={100} />
              <Skeleton variant="line" count={3} />
            </div>

            {/* On-chain transaction */}
            <div className="payout-card">
              <Skeleton variant="text" width={140} />
              <Skeleton variant="line" count={2} />
            </div>

            {/* Activity */}
            <div className="payout-card">
              <Skeleton variant="text" width={100} />
              <Skeleton variant="line" count={3} />
            </div>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (notFound || !payout) {
    return (
      <div className="ent ent-page payout-detail-page">
        <div className="not-found-state">
          <CreditCard size={48} />
          <h2>Payout Not Found</h2>
          <p>This payout request doesn&apos;t exist or you don&apos;t have access to it.</p>
          <button className="ent-btn ent-btn-primary" onClick={() => navigate(backTo)}>
            <ArrowLeft size={18} />
            Back to {backLabel}
          </button>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  const fee = rowFee(payout);
  const net = rowNet(payout);
  const rejection = pick(payout, 'rejectionReason', 'rejection_reason');
  const heldReason = pick(payout, 'heldReason', 'held_reason');
  const fromAddress = pick(payout, 'fromAddress', 'from_address');
  const explorerUrl = payout.network?.explorerUrl;
  const events = buildEvents(payout);
  const isPending = payout.status === 'pending';

  return (
    <div className="ent ent-page payout-detail-page">
      <div className="payout-breadcrumb-bar">
        <button className="back-btn" onClick={() => navigate(backTo)} aria-label={`Back to ${backLabel}`}>
          <ChevronLeft size={18} />
        </button>
        <span className="breadcrumb-link" onClick={() => navigate(backTo)}>{backLabel}</span>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current mono">{payout.requestId}</span>
      </div>

      <div className="ent-page-head">
        <div>
          <div className="po-head-title">
            <h1 className="ent-page-head__title mono">{payout.requestId}</h1>
            <StatusBadge status={payout.status} dot />
            {payout.isTestnet && <span className="testnet-badge">Testnet</span>}
            {payout.priority && <span className="priority-badge">Priority</span>}
          </div>
          <div className="po-head-sub">
            To{' '}
            <span className="mono">
              {payout.destinationAddress ? truncateHash(payout.destinationAddress, 6, 6) : '—'}
            </span>
            {' · Requested '}
            {formatDate(payout.createdAt)}
          </div>
        </div>
        <div className="ent-page-head__actions">
          {isPending && isSuperAdmin && !payout.txHash && (
            <>
              <button className="ent-btn ent-btn-primary" onClick={handleApproveAndPay} disabled={actionLoading}>
                {actionLoading && payoutStep ? (
                  <>
                    <Loader2 size={16} className="spinning" />
                    {payoutStep === 'switching' && 'Switching…'}
                    {payoutStep === 'preparing' && 'Preparing…'}
                    {payoutStep === 'signing' && 'Sign in wallet…'}
                    {payoutStep === 'confirming' && 'Confirming…'}
                    {payoutStep === 'completing' && 'Completing…'}
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Approve &amp; pay
                  </>
                )}
              </button>
              <div className="po-menu-wrap">
                <button
                  className="ent-btn ent-btn-secondary po-menu-btn"
                  onClick={() => setShowActionsMenu((v) => !v)}
                  aria-label="More actions"
                  aria-haspopup="menu"
                  disabled={actionLoading}
                >
                  <MoreHorizontal size={18} />
                </button>
                {showActionsMenu && (
                  <>
                    <div className="po-menu-overlay" onClick={() => setShowActionsMenu(false)} />
                    <div className="po-menu" role="menu">
                      <button role="menuitem" onClick={() => { setShowActionsMenu(false); setShowManualModal(true); }}>
                        <Hash size={15} /> Approve manual
                      </button>
                      <button role="menuitem" onClick={() => { setShowActionsMenu(false); handleRecheck(); }}>
                        <Search size={15} /> Recheck status
                      </button>
                      <div className="po-menu-sep" />
                      <button role="menuitem" className="po-menu-danger" onClick={() => { setShowActionsMenu(false); setShowRejectModal(true); }}>
                        <XCircle size={15} /> Reject
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          {isPending && !isSuperAdmin && (
            <button className="ent-btn ent-btn-ghost-danger" onClick={handleCancel} disabled={actionLoading}>
              {actionLoading ? <Loader2 size={16} className="spinning" /> : <XCircle size={16} />}
              Cancel request
            </button>
          )}
          {payout.txHash && explorerUrl && (
            <a
              className="ent-btn ent-btn-secondary"
              href={`${explorerUrl}/tx/${payout.txHash}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={16} />
              View on explorer
            </a>
          )}
        </div>
      </div>

      <div className="payout-layout">
        <div className="payout-main-content">
          {/* Overview: net payout + compact breakdown (left) · details (right) */}
          <div className="payout-card po-overview">
            <div className="po-overview-amount">
              <span className="po-amount-label">Net payout</span>
              <span className="amount-large ent-num">
                {net != null ? `$${formatUSD(net)}` : `$${formatUSD(payout.amount)}`}
              </span>
              {payout.tokenAmount && (
                <span className="token-amount">
                  {formatCrypto(parseFloat(payout.tokenAmount))} {payout.tokenSymbol}
                </span>
              )}
              {payout.conversionRate && (
                <span className="conversion-rate">
                  Rate: 1 {payout.tokenSymbol} = ${formatUSD(payout.conversionRate)}
                </span>
              )}
              <div className="po-mini-breakdown">
                <div><span>Gross</span><span className="ent-num">${formatUSD(payout.amount)}</span></div>
                <div><span>Fee</span><span className="ent-num">{fee != null ? `-$${formatUSD(fee)}` : '—'}</span></div>
              </div>
            </div>
            <div className="po-overview-details">
              <div className="summary-field">
                <label>Destination</label>
                <div className="po-dest">
                  <span className="mono address-full">{payout.destinationAddress || '—'}</span>
                  <button
                    className="po-copy-icon"
                    onClick={handleCopyAddress}
                    title="Copy address"
                    aria-label="Copy address"
                    disabled={!payout.destinationAddress}
                  >
                    {copiedAddr ? <CheckCircle size={14} /> : <Copy size={14} />}
                  </button>
                </div>
                <span className="summary-subtext dest-token">
                  {payout.tokenSymbol && <TokenChainLogo symbol={payout.tokenSymbol} chain={payout.networkName} size={16} />}
                  {payout.tokenSymbol || 'Wallet'}{payout.networkName ? ` · ${payout.networkName}` : ''}
                </span>
              </div>
              <div className="po-details-grid">
                {isSuperAdmin && payout.merchant && (
                  <div className="summary-field">
                    <label>Requested by</label>
                    <span className="summary-value">
                      {payout.merchant.name || 'Unknown'}
                      {payout.merchant.email && (
                        <span className="summary-subtext">{payout.merchant.email}</span>
                      )}
                    </span>
                  </div>
                )}
                <div className="summary-field">
                  <label>Request</label>
                  <span className="summary-value mono">{payout.requestId}</span>
                </div>
                <div className="summary-field">
                  <label>Created</label>
                  <span className="summary-value">{formatDateTime(payout.createdAt)}</span>
                </div>
                {payout.completedAt && (
                  <div className="summary-field">
                    <label>Completed</label>
                    <span className="summary-value">{formatDateTime(payout.completedAt)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Merchant balance — where this payout sits in the merchant's balance.
              Rendered as a visible derivation rather than three bare figures, so
              a merchant and an admin reading the same card reach the same number:

                Settled balance
                − Other pending payouts     (shown only when non-zero)
                − Refunds reserved          (shown only when non-zero)
                = Balance before this payout
                − This payout
                = Balance after this payout

              The two deduction rows are what made the old card unreadable: they
              were applied by the API but never shown, so `available` looked
              arbitrary against the merchant's settled total. */}
          {payout.merchantBalance && (
            <div className="payout-card po-balance-card">
              <h3>
                Merchant balance
                {payout.merchantBalance.isTestnet ? <span className="po-balance-mode">Testnet</span> : null}
              </h3>

              <div className="po-balance-rows">
                {/* Only worth showing the settled figure and its deductions when
                    something is actually deducted; otherwise it just restates the
                    row below it. */}
                {balanceHasAdjustments && (
                  <>
                    <div className="po-balance-row">
                      <span>
                        Settled balance
                        <span className="po-balance-hint">Confirmed payments, less paid-out and refunded amounts</span>
                      </span>
                      <span className="ent-num">${formatUSD(payout.merchantBalance.settledBalance)}</span>
                    </div>
                    {payout.merchantBalance.otherPendingPayouts > 0 && (
                      <div className="po-balance-row po-balance-row--sub">
                        <span>
                          Other pending payouts
                          <span className="po-balance-hint">Held for payout requests still in progress</span>
                        </span>
                        <span className="ent-num po-balance-neg">
                          −${formatUSD(payout.merchantBalance.otherPendingPayouts)}
                        </span>
                      </div>
                    )}
                    {payout.merchantBalance.reservedRefundAmount > 0 && (
                      <div className="po-balance-row po-balance-row--sub">
                        <span>
                          Refunds reserved
                          <span className="po-balance-hint">Held for refunds that have not settled yet</span>
                        </span>
                        <span className="ent-num po-balance-neg">
                          −${formatUSD(payout.merchantBalance.reservedRefundAmount)}
                        </span>
                      </div>
                    )}
                  </>
                )}

                <div className={`po-balance-row${balanceHasAdjustments ? ' po-balance-subtotal' : ''}`}>
                  <span>Balance before this payout</span>
                  <span className="ent-num">${formatUSD(payout.merchantBalance.available)}</span>
                </div>

                {payout.merchantBalance.appliesToBalance === false ? (
                  <div className="po-balance-row">
                    <span>This payout</span>
                    <span className="ent-num po-balance-muted">Not deducted</span>
                  </div>
                ) : (
                  <>
                    <div className="po-balance-row">
                      <span>
                        This payout
                        <span className="po-balance-hint">
                          {payout.merchantBalance.alreadyDebited
                            ? 'Paid and taken from the balance'
                            : 'Held back until this payout completes'}
                        </span>
                      </span>
                      <span className="ent-num po-balance-neg">−${formatUSD(payout.merchantBalance.thisPayout)}</span>
                    </div>
                    <div className="po-balance-row po-balance-after">
                      <span>Balance after this payout</span>
                      <span className="ent-num">${formatUSD(payout.merchantBalance.afterPayout)}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Which side of the debit the reader is looking at. Without this,
                  "before" and "after" give no clue which one is live today. */}
              <div
                className={`po-balance-note${
                  payout.merchantBalance.appliesToBalance === false ? ' po-balance-note--void' : ''
                }`}
              >
                {payout.merchantBalance.appliesToBalance === false ? (
                  <>
                    <XCircle size={14} />
                    <span>
                      This payout was {payout.status} and never left the balance. The merchant&apos;s available
                      balance is ${formatUSD(payout.merchantBalance.available)}.
                    </span>
                  </>
                ) : payout.merchantBalance.alreadyDebited ? (
                  <>
                    <CheckCircle size={14} />
                    <span>
                      Paid{payout.completedAt ? ` on ${formatDateTime(payout.completedAt)}` : ''}. The merchant&apos;s
                      available balance is now ${formatUSD(payout.merchantBalance.afterPayout)}.
                    </span>
                  </>
                ) : (
                  <>
                    <Clock size={14} />
                    <span>
                      Not sent yet, but already held back. The merchant can currently withdraw $
                      {formatUSD(payout.merchantBalance.afterPayout)}.
                    </span>
                  </>
                )}
              </div>

              {/* Lifetime figures, so this payout can be placed against the
                  merchant's overall history without leaving the page. */}
              <div className="po-balance-lifetime">
                <div className="po-balance-lifetime__item">
                  <span className="po-balance-lifetime__label">Received to date</span>
                  <span className="ent-num">${formatUSD(payout.merchantBalance.totalReceived)}</span>
                </div>
                <div className="po-balance-lifetime__item">
                  <span className="po-balance-lifetime__label">Paid out to date</span>
                  <span className="ent-num">${formatUSD(payout.merchantBalance.totalWithdrawn)}</span>
                </div>
              </div>

              {balanceHistoryTo && (
                <button type="button" className="po-balance-link" onClick={() => navigate(balanceHistoryTo)}>
                  {isSuperAdmin ? "View this merchant's payout history" : 'View your payout history'}
                </button>
              )}
            </div>
          )}

          {/* On-chain transaction */}
          <div className="payout-card">
            <h3>On-chain transaction</h3>
            {payout.status === 'processing' && payout.txHash && (
              <div className="po-confirm-meter">
                <div className="po-confirm-head">
                  <span className="po-confirm-dot" />
                  <span className="po-confirm-title">Confirming on-chain</span>
                  <span className="po-confirm-count">
                    {onchain
                      ? `${Math.min(onchain.confirmations, onchain.required)} / ${onchain.required}`
                      : '…'} confirmations
                  </span>
                </div>
                <div className="po-confirm-bar">
                  <div
                    className="po-confirm-fill"
                    style={{
                      width: `${onchain
                        ? Math.min(100, Math.round((onchain.confirmations / Math.max(onchain.required, 1)) * 100))
                        : 6}%`,
                    }}
                  />
                </div>
                <p className="po-confirm-note">
                  Auto-checking every minute — this flips to Completed once fully confirmed.
                </p>
              </div>
            )}
            {payout.txHash ? (
              <div className="pro-table-wrap po-table-wrap">
                <table className="pro-table">
                  <thead>
                    <tr>
                      <th>STATUS</th>
                      <th className="amount-cell">AMOUNT</th>
                      <th>TOKEN</th>
                      <th>TO</th>
                      <th>TX HASH</th>
                      <th>DATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td data-label="Status">
                        <StatusBadge status={payout.status} dot />
                      </td>
                      <td className="amount-cell" data-label="Amount">
                        <span className="amount-cell__main ent-num">
                          {payout.tokenAmount ? formatCrypto(parseFloat(payout.tokenAmount)) : '—'}
                        </span>
                      </td>
                      <td data-label="Token">
                        <span className="token-chip">
                          <TokenChainLogo
                            symbol={payout.tokenSymbol}
                            chain={payout.networkName || payout.network?.name}
                            size={18}
                          />
                          <span>{payout.tokenSymbol || '—'}</span>
                        </span>
                      </td>
                      <td data-label="To">
                        <span className="mono text-muted-cell" title={payout.destinationAddress}>
                          {payout.destinationAddress ? truncateHash(payout.destinationAddress, 6, 4) : '—'}
                        </span>
                      </td>
                      <td data-label="Tx hash">
                        {explorerUrl ? (
                          <a
                            href={`${explorerUrl}/tx/${payout.txHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tx-hash-link mono"
                            title={payout.txHash}
                          >
                            {truncateHash(payout.txHash, 8, 6)}
                            <ExternalLink size={12} />
                          </a>
                        ) : (
                          <span className="mono text-muted-cell" title={payout.txHash}>
                            {truncateHash(payout.txHash, 8, 6)}
                          </span>
                        )}
                      </td>
                      <td data-label="Date">
                        <span className="text-muted-cell">{formatDate(payout.completedAt || payout.reviewedAt)}</span>
                      </td>
                    </tr>
                    {fromAddress && (
                      <tr>
                        <td colSpan="6" className="po-from-row">
                          Paid from <span className="mono">{truncateHash(fromAddress, 8, 6)}</span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="po-empty">
                <span className="po-empty-ic">
                  <Send size={20} />
                </span>
                <h4>No on-chain transaction yet</h4>
                <p>
                  {isSuperAdmin
                    ? 'Approve & pay above to broadcast this payout. The transaction hash and confirmations will appear here.'
                    : 'Once an operator approves this payout, the on-chain transaction will appear here with its hash and confirmation.'}
                </p>
              </div>
            )}
          </div>

          {/* Activity */}
          <div className="payout-card">
            <h3>Activity</h3>
            <div className="activity-list">
              {events.map((event, index) => (
                <div key={index} className="activity-item">
                  {getEventIcon(event.type)}
                  <div className="activity-content">
                    <span className="activity-message">{event.message}</span>
                    <span className="activity-date">{formatDateTime(event.date)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Rejection reason */}
          {rejection && (
            <div className="payout-card rejection-card">
              <h3>Rejection reason</h3>
              <p>{rejection}</p>
            </div>
          )}

          {/* Hold reason */}
          {heldReason && (
            <div className="payout-card held-card">
              <h3>Hold reason</h3>
              <p>{heldReason}</p>
            </div>
          )}

          {/* Admin notes */}
          {payout.adminNotes && (
            <div className="payout-card">
              <h3>Admin notes</h3>
              <p className="notes-text">{payout.adminNotes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Reject modal — reason required */}
      {showRejectModal && (
        <>
          <div className="modal-overlay" onClick={() => !actionLoading && setShowRejectModal(false)} />
          <div className="action-modal">
            <div className="modal-header">
              <h3>Reject payout request</h3>
              <button className="close-btn" onClick={() => setShowRejectModal(false)} disabled={actionLoading}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Rejection reason *</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection (shown to the merchant)…"
                  rows={3}
                  disabled={actionLoading}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="ent-btn ent-btn-secondary"
                onClick={() => setShowRejectModal(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="ent-btn ent-btn-danger"
                onClick={handleReject}
                disabled={actionLoading || !rejectionReason.trim()}
              >
                {actionLoading ? (
                  <>
                    <Loader2 size={16} className="spinning" />
                    Rejecting...
                  </>
                ) : (
                  'Reject request'
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Approve manual — verify a pasted tx hash */}
      {showManualModal && (
        <>
          <div className="modal-overlay" onClick={() => !actionLoading && setShowManualModal(false)} />
          <div className="action-modal">
            <div className="modal-header">
              <h3>Approve manual</h3>
              <button className="close-btn" onClick={() => setShowManualModal(false)} disabled={actionLoading}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p className="modal-hint">
                Paste the hash of the payout you already sent. We verify the amount, destination, and
                status on-chain before marking it paid.
              </p>
              <div className="form-group">
                <label>Transaction hash *</label>
                <input
                  className="mono"
                  value={manualTxHash}
                  onChange={(e) => setManualTxHash(e.target.value)}
                  placeholder="0x…"
                  disabled={actionLoading}
                />
              </div>
              <div className="form-group">
                <label>From address (optional)</label>
                <input
                  className="mono"
                  value={manualFromAddress}
                  onChange={(e) => setManualFromAddress(e.target.value)}
                  placeholder="0x… (sender wallet)"
                  disabled={actionLoading}
                />
              </div>
              <div className="manual-expected">
                Expecting{' '}
                <b>
                  {payout.tokenAmount ? formatCrypto(parseFloat(payout.tokenAmount)) : '—'} {payout.tokenSymbol}
                </b>{' '}
                to <span className="mono">{payout.destinationAddress ? truncateHash(payout.destinationAddress, 6, 4) : '—'}</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="ent-btn ent-btn-secondary" onClick={() => setShowManualModal(false)} disabled={actionLoading}>
                Cancel
              </button>
              <button
                className="ent-btn ent-btn-primary"
                onClick={handleApproveManual}
                disabled={actionLoading || !manualTxHash.trim()}
              >
                {actionLoading ? (
                  <>
                    <Loader2 size={16} className="spinning" />
                    Verifying…
                  </>
                ) : (
                  'Verify & mark paid'
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Recheck status — scan destination for the paying transfer */}
      {showRecheckModal && (
        <>
          <div className="modal-overlay" onClick={() => !mappingHash && setShowRecheckModal(false)} />
          <div className="action-modal recheck-modal">
            <div className="modal-header">
              <h3>Recheck status</h3>
              <button className="close-btn" onClick={() => setShowRecheckModal(false)} disabled={!!mappingHash}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              {recheckLoading ? (
                <div className="recheck-loading">
                  <Loader2 size={20} className="spinning" />
                  Scanning the destination address…
                </div>
              ) : recheckResult && recheckResult.scanAvailable && recheckResult.candidates?.length ? (
                <>
                  <p className="modal-hint">
                    Transfers of{' '}
                    <b>
                      {recheckResult.expected
                        ? `${formatCrypto(recheckResult.expected.amount)} ${recheckResult.expected.asset}`
                        : ''}
                    </b>{' '}
                    to the destination. Select the one that paid this payout.
                  </p>
                  <div className="recheck-list">
                    {recheckResult.candidates.map((c) => (
                      <div className="recheck-item" key={c.txHash}>
                        <div className="recheck-item-main">
                          <span className="recheck-amount ent-num">
                            {formatCrypto(c.amount)} {c.asset}
                          </span>
                          <span className="recheck-meta">
                            {c.confirmations} confs · {new Date(c.timestamp).toLocaleString()}
                          </span>
                          <span className="mono recheck-hash">{truncateHash(c.txHash, 10, 8)}</span>
                        </div>
                        <button
                          className="ent-btn ent-btn-primary"
                          onClick={() => handleMapCandidate(c.txHash)}
                          disabled={!!mappingHash}
                        >
                          {mappingHash === c.txHash ? (
                            <>
                              <Loader2 size={14} className="spinning" />
                              Mapping…
                            </>
                          ) : (
                            'This one'
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="recheck-empty">
                  <Search size={28} />
                  <p className="recheck-empty-title">
                    {recheckResult && !recheckResult.scanAvailable
                      ? 'On-chain scan unavailable'
                      : 'No matching transfer found'}
                  </p>
                  <p className="recheck-empty-note">
                    {recheckResult && !recheckResult.scanAvailable
                      ? 'Automatic scanning needs a block-explorer API key for this network. Use “Approve manual” and paste the transaction hash instead.'
                      : 'No transfer of this amount to the destination address yet. If you have the hash, use “Approve manual”.'}
                  </p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="ent-btn ent-btn-secondary" onClick={() => setShowRecheckModal(false)} disabled={!!mappingHash}>
                Close
              </button>
              {recheckResult && !recheckLoading && (!recheckResult.scanAvailable || !recheckResult.candidates?.length) && (
                <button
                  className="ent-btn ent-btn-primary"
                  onClick={() => { setShowRecheckModal(false); setShowManualModal(true); }}
                >
                  <Hash size={16} />
                  Approve manual
                </button>
              )}
            </div>
          </div>
        </>
      )}

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .payout-detail-page .loading-state,
  .payout-detail-page .not-found-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    color: var(--ent-text-2);
    gap: 1rem;
    text-align: center;
  }

  .payout-detail-page .not-found-state h2 { margin: 0; color: var(--ent-text); }
  .payout-detail-page .not-found-state p { margin: 0 0 1rem 0; }

  .payout-detail-page .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--ent-border);
    border-top-color: var(--ent-text-2);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  .payout-detail-page .spinning { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Breadcrumb */
  .payout-detail-page .payout-breadcrumb-bar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    font-size: 0.8125rem;
    color: var(--ent-text-2);
  }
  .payout-detail-page .back-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: var(--ent-card);
    border: 1px solid var(--ent-border-strong);
    border-radius: var(--ent-radius-sm);
    cursor: pointer;
    color: var(--ent-text-2);
  }
  .payout-detail-page .back-btn:hover { background: var(--ent-card-alt); color: var(--ent-text); }
  .payout-detail-page .breadcrumb-link { color: var(--ent-text-2); cursor: pointer; }
  .payout-detail-page .breadcrumb-link:hover { color: var(--ent-text); text-decoration: underline; }
  .payout-detail-page .breadcrumb-separator { color: var(--ent-muted); }
  .payout-detail-page .breadcrumb-current { color: var(--ent-text); font-weight: 500; }

  /* Header */
  .payout-detail-page .po-head-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .payout-detail-page .po-head-sub {
    margin-top: 6px;
    font-size: 13px;
    color: var(--ent-text-2);
  }
  .payout-detail-page .testnet-badge {
    display: inline-block;
    padding: 0.125rem 0.375rem;
    background: var(--ent-amber-bg);
    color: var(--ent-amber);
    border-radius: 4px;
    font-size: 0.65rem;
    font-weight: 600;
  }
  .payout-detail-page .priority-badge {
    display: inline-block;
    padding: 0.125rem 0.375rem;
    background: var(--brand-100, rgba(124, 58, 237, 0.12));
    color: var(--brand-700, #6d28d9);
    border-radius: 4px;
    font-size: 0.65rem;
    font-weight: 600;
    margin-left: 0.375rem;
  }

  /* Layout */
  .payout-detail-page .payout-layout { display: flex; gap: 1.25rem; }
  .payout-detail-page .payout-main-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  /* Cards */
  .payout-detail-page .payout-card {
    background: var(--ent-card);
    border: 1px solid var(--ent-border);
    border-radius: var(--ent-radius);
    box-shadow: var(--ent-shadow);
    padding: 18px 20px;
  }
  .payout-detail-page .payout-card h3 {
    margin: 0 0 1rem 0;
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--ent-text);
  }

  /* Amount */
  .payout-detail-page .amount-display { display: flex; flex-direction: column; gap: 0.35rem; }
  .payout-detail-page .amount-large {
    font-size: 2rem;
    font-weight: 700;
    color: var(--ent-text);
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  }
  .payout-detail-page .token-amount { font-size: 1rem; color: var(--ent-text-2); }
  .payout-detail-page .conversion-rate { font-size: 0.8rem; color: var(--ent-muted); }

  /* Header actions dropdown (⋯) */
  .payout-detail-page .po-menu-wrap { position: relative; }
  .payout-detail-page .po-menu-btn { padding-left: 0.6rem; padding-right: 0.6rem; }
  .payout-detail-page .po-menu-overlay { position: fixed; inset: 0; z-index: 40; }
  .payout-detail-page .po-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 6px);
    z-index: 41;
    min-width: 210px;
    padding: 6px;
    background: var(--ent-surface, #fff);
    border: 1px solid var(--ent-border, #e5e7eb);
    border-radius: 12px;
    box-shadow: 0 12px 32px rgba(16, 18, 40, 0.14);
    display: flex;
    flex-direction: column;
  }
  .payout-detail-page .po-menu button {
    display: flex; align-items: center; gap: 0.55rem;
    padding: 0.55rem 0.6rem; border: none; background: none; text-align: left;
    border-radius: 8px; font: inherit; font-size: 0.88rem; color: var(--ent-text-1, #0f172a); cursor: pointer;
  }
  .payout-detail-page .po-menu button:hover { background: var(--ent-bg-2, #f4f5fb); }
  .payout-detail-page .po-menu .po-menu-danger { color: var(--ent-red, #ef4444); }
  .payout-detail-page .po-menu .po-menu-danger:hover { background: rgba(239, 68, 68, 0.08); }
  .payout-detail-page .po-menu-sep { height: 1px; background: var(--ent-border, #e5e7eb); margin: 4px 2px; }

  /* Overview: amount + breakdown (left) · details (right) */
  .payout-detail-page .po-overview { display: flex; gap: 2rem; }
  .payout-detail-page .po-overview-amount {
    flex: 0 0 auto; min-width: 220px;
    border-right: 1px solid var(--ent-border, #e5e7eb);
    padding-right: 2rem;
    display: flex; flex-direction: column; gap: 0.35rem;
  }
  .payout-detail-page .po-amount-label {
    font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em;
    font-weight: 700; color: var(--ent-text-2, #64748b); margin-bottom: 0.15rem;
  }
  .payout-detail-page .po-mini-breakdown {
    margin-top: 0.9rem; padding-top: 0.8rem; border-top: 1px solid var(--ent-border, #e5e7eb);
    display: flex; flex-direction: column; gap: 0.4rem;
  }
  .payout-detail-page .po-mini-breakdown div { display: flex; justify-content: space-between; gap: 1.5rem; font-size: 0.85rem; color: var(--ent-text-2, #64748b); }
  .payout-detail-page .po-mini-breakdown .ent-num { color: var(--ent-text-1, #0f172a); }
  .payout-detail-page .po-overview-details { flex: 1 1 auto; min-width: 0; }
  .payout-detail-page .po-dest { display: flex; align-items: center; gap: 0.4rem; }
  .payout-detail-page .po-dest .address-full { word-break: break-all; }
  .payout-detail-page .po-copy-icon {
    border: none; background: none; color: var(--ent-text-2, #94a3b8); cursor: pointer;
    padding: 2px; display: inline-flex; flex-shrink: 0;
  }
  .payout-detail-page .po-copy-icon:hover { color: var(--ent-text-1, #0f172a); }
  .payout-detail-page .po-details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem 2rem; margin-top: 1.1rem; }

  /* Merchant balance card */
  .payout-detail-page .po-balance-card h3 { display: flex; align-items: center; gap: 0.5rem; }
  .payout-detail-page .po-balance-mode {
    font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    padding: 2px 7px; border-radius: 999px; background: var(--warn-soft, #fef3e0); color: var(--warn-ink, #b9760a);
  }
  .payout-detail-page .po-balance-rows { display: flex; flex-direction: column; }
  .payout-detail-page .po-balance-row {
    display: flex; justify-content: space-between; align-items: flex-start; gap: 1.5rem;
    padding: 0.55rem 0; font-size: 0.92rem; color: var(--ent-text-2);
  }
  /* Row label + its explanatory hint stack; the figure stays on the baseline. */
  .payout-detail-page .po-balance-row > span:first-child {
    display: flex; flex-direction: column; gap: 2px; min-width: 0;
  }
  .payout-detail-page .po-balance-row .ent-num { color: var(--ent-text); white-space: nowrap; }
  .payout-detail-page .po-balance-hint {
    font-size: 0.75rem; line-height: 1.35; color: var(--ent-muted); font-weight: 400;
  }
  /* Deductions sit under the figure they reduce. */
  .payout-detail-page .po-balance-row--sub > span:first-child { padding-left: 0.85rem; }
  .payout-detail-page .po-balance-neg { color: var(--ent-red) !important; }
  .payout-detail-page .po-balance-muted { color: var(--ent-muted) !important; font-weight: 500; }
  /* Running subtotal: the "= before this payout" line closing the deductions. */
  .payout-detail-page .po-balance-subtotal {
    border-top: 1px solid var(--ent-border); margin-top: 0.25rem; padding-top: 0.7rem;
    font-weight: 600; color: var(--ent-text);
  }
  .payout-detail-page .po-balance-after {
    border-top: 1px solid var(--ent-border); margin-top: 0.25rem; padding-top: 0.7rem;
    font-weight: 700; color: var(--ent-text); font-size: 1rem;
  }
  .payout-detail-page .po-balance-note {
    display: flex; align-items: flex-start; gap: 0.5rem; margin-top: 0.9rem;
    padding: 0.6rem 0.7rem; border-radius: var(--ent-radius-sm);
    background: var(--ent-card-alt); border: 1px solid var(--ent-border);
    font-size: 0.8rem; line-height: 1.45; color: var(--ent-text-2);
  }
  .payout-detail-page .po-balance-note svg { flex-shrink: 0; margin-top: 1px; color: var(--ent-muted); }
  .payout-detail-page .po-balance-note--void svg { color: var(--ent-red); }
  .payout-detail-page .po-balance-lifetime {
    display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem;
    margin-top: 0.9rem; padding-top: 0.85rem; border-top: 1px solid var(--ent-border);
  }
  .payout-detail-page .po-balance-lifetime__item { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .payout-detail-page .po-balance-lifetime__label {
    font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em;
    font-weight: 600; color: var(--ent-muted);
  }
  .payout-detail-page .po-balance-lifetime .ent-num { font-size: 0.92rem; color: var(--ent-text); }
  .payout-detail-page .po-balance-link {
    margin-top: 0.85rem; padding: 0; background: none; border: none; cursor: pointer;
    font-size: 0.8rem; font-weight: 600; color: var(--ent-primary);
    font-family: inherit; text-align: left;
  }
  .payout-detail-page .po-balance-link:hover { text-decoration: underline; }
  @media (max-width: 480px) {
    .payout-detail-page .po-balance-lifetime { grid-template-columns: 1fr; gap: 0.6rem; }
  }

  /* Actions card */
  .payout-detail-page .actions-card .wallet-status {
    margin-bottom: 14px;
    padding: 12px 14px;
    background: var(--ent-card-alt);
    border: 1px solid var(--ent-border);
    border-radius: var(--ent-radius-sm);
  }
  .payout-detail-page .wallet-connected {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
  .payout-detail-page .wallet-address {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: var(--ent-text);
  }
  .payout-detail-page .wallet-not-connected {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  .payout-detail-page .wallet-not-connected span { font-size: 0.8rem; color: var(--ent-text-2); }
  .payout-detail-page .btn-link {
    background: transparent;
    border: none;
    color: var(--ent-muted);
    cursor: pointer;
    font-size: 0.75rem;
    text-decoration: underline;
  }
  .payout-detail-page .btn-link:hover { color: var(--ent-text); }
  .payout-detail-page .po-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .payout-detail-page .po-actions .ent-btn { justify-content: center; }
  .payout-detail-page .po-action-primary { width: 100%; }
  .payout-detail-page .po-actions-secondary { display: flex; gap: 0.5rem; }
  .payout-detail-page .po-actions-secondary .ent-btn { flex: 1 1 0; min-width: 0; }
  .payout-detail-page .po-action-reject { width: 100%; margin-top: 0.15rem; }
  .payout-detail-page .ent-btn-danger { background: var(--ent-red, #ef4444); color: #fff; border-color: transparent; }
  .payout-detail-page .ent-btn-danger:hover:not(:disabled) { background: #dc2626; }
  .payout-detail-page .ent-btn-ghost-danger {
    background: transparent;
    color: var(--ent-red, #ef4444);
    border: 1px solid rgba(239, 68, 68, 0.35);
  }
  .payout-detail-page .ent-btn-ghost-danger:hover:not(:disabled) { background: rgba(239, 68, 68, 0.08); }

  /* Live confirmation meter */
  .payout-detail-page .po-confirm-meter {
    background: var(--ent-bg-2, #f8fafc);
    border: 1px solid var(--ent-border, #e5e7eb);
    border-radius: var(--ent-radius-sm, 10px);
    padding: 0.875rem 1rem;
    margin-bottom: 1rem;
  }
  .payout-detail-page .po-confirm-head { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.6rem; }
  .payout-detail-page .po-confirm-dot {
    width: 9px; height: 9px; border-radius: 50%;
    background: var(--ent-amber, #f59e0b);
    box-shadow: 0 0 0 3px rgba(245, 165, 36, 0.18);
    animation: poConfirmPulse 1.6s ease-in-out infinite;
    flex-shrink: 0;
  }
  @keyframes poConfirmPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
  .payout-detail-page .po-confirm-title { font-weight: 700; color: var(--ent-text-1, #0f172a); font-size: 0.9rem; }
  .payout-detail-page .po-confirm-count { margin-left: auto; font-size: 0.8rem; color: var(--ent-text-2, #64748b); font-variant-numeric: tabular-nums; }
  .payout-detail-page .po-confirm-bar { height: 6px; border-radius: 999px; background: var(--ent-border, #e5e7eb); overflow: hidden; }
  .payout-detail-page .po-confirm-fill {
    height: 100%; border-radius: 999px;
    background: linear-gradient(90deg, #8270ff, #5847d6);
    transition: width 0.4s ease;
  }
  .payout-detail-page .po-confirm-note { margin: 0.5rem 0 0; font-size: 0.78rem; color: var(--ent-text-2, #64748b); }

  /* Modal extras (manual + recheck) */
  .payout-detail-page .modal-hint { margin: 0 0 0.875rem; font-size: 0.85rem; color: var(--ent-text-2, #64748b); line-height: 1.5; }
  .payout-detail-page .action-modal input.mono { font-family: var(--ent-font-mono, 'JetBrains Mono', monospace); font-size: 0.85rem; }
  .payout-detail-page .manual-expected {
    margin-top: 0.5rem; padding: 0.6rem 0.75rem; border-radius: 8px;
    background: var(--ent-bg-2, #f8fafc); font-size: 0.82rem; color: var(--ent-text-2, #64748b);
  }
  .payout-detail-page .manual-expected b { color: var(--ent-text-1, #0f172a); }
  .payout-detail-page .recheck-modal { max-width: 560px; }
  .payout-detail-page .recheck-loading { display: flex; align-items: center; gap: 0.6rem; padding: 1.5rem 0.5rem; color: var(--ent-text-2, #64748b); font-size: 0.9rem; }
  .payout-detail-page .recheck-list { display: flex; flex-direction: column; gap: 0.5rem; }
  .payout-detail-page .recheck-item {
    display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;
    padding: 0.75rem 0.875rem; border: 1px solid var(--ent-border, #e5e7eb); border-radius: 10px;
  }
  .payout-detail-page .recheck-item-main { display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; }
  .payout-detail-page .recheck-amount { font-weight: 700; color: var(--ent-text-1, #0f172a); font-size: 0.95rem; }
  .payout-detail-page .recheck-meta { font-size: 0.75rem; color: var(--ent-text-2, #64748b); }
  .payout-detail-page .recheck-hash { font-size: 0.72rem; color: var(--ent-text-2, #94a3b8); }
  .payout-detail-page .recheck-item .ent-btn { flex-shrink: 0; }
  .payout-detail-page .recheck-empty { text-align: center; padding: 1.5rem 1rem; }
  .payout-detail-page .recheck-empty svg { color: var(--ent-text-2, #94a3b8); margin-bottom: 0.5rem; }
  .payout-detail-page .recheck-empty-title { margin: 0 0 0.35rem; font-weight: 700; color: var(--ent-text-1, #0f172a); font-size: 0.95rem; }
  .payout-detail-page .recheck-empty-note { margin: 0 auto; max-width: 380px; font-size: 0.82rem; color: var(--ent-text-2, #64748b); line-height: 1.5; }
  .payout-detail-page .already-processed-notice {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.875rem;
    background: var(--ent-green-bg, rgba(16,185,129,0.1));
    border: 1px solid var(--ent-green, #10b981);
    border-radius: var(--ent-radius-sm);
    flex: 1 1 100%;
  }
  .payout-detail-page .already-processed-notice svg { color: var(--ent-green, #10b981); flex-shrink: 0; margin-top: 0.125rem; }
  .payout-detail-page .already-processed-notice > div { display: flex; flex-direction: column; gap: 0.25rem; }
  .payout-detail-page .already-processed-notice .notice-title { font-weight: 600; color: var(--ent-green, #10b981); font-size: 0.9rem; }
  .payout-detail-page .already-processed-notice .notice-text { font-size: 0.8rem; color: var(--ent-text-2); }

  /* Summary */
  .payout-detail-page .summary-grid { display: flex; gap: 3rem; }
  .payout-detail-page .summary-left { flex: 1; min-width: 0; }
  .payout-detail-page .summary-right { flex: 1; display: flex; flex-direction: column; gap: 0.875rem; }
  .payout-detail-page .summary-field label {
    display: block;
    font-size: 0.75rem;
    color: var(--ent-muted);
    margin-bottom: 0.25rem;
  }
  .payout-detail-page .summary-value { color: var(--ent-text); font-size: 0.875rem; display: flex; flex-direction: column; gap: 2px; align-items: flex-start; }
  .payout-detail-page .summary-subtext { color: var(--ent-text-2); font-size: 0.8rem; }
  .payout-detail-page .address-full { word-break: break-all; font-size: 0.8rem; }
  .payout-detail-page .dest-token { display: inline-flex; align-items: center; gap: 6px; }

  /* Breakdown / totals */
  .payout-detail-page .po-totals { margin: 0; padding: 0; border: none; max-width: 420px; margin-left: auto; }
  .payout-detail-page .total-row {
    display: flex;
    justify-content: space-between;
    padding: 0.375rem 0;
    font-size: 0.875rem;
    color: var(--ent-text-2);
  }
  .payout-detail-page .total-row.total-net {
    font-weight: 700;
    color: var(--ent-text);
    padding-top: 0.625rem;
    margin-top: 0.25rem;
    border-top: 1px solid var(--ent-border);
  }
  .payout-detail-page .po-receipt-note {
    margin: 0.75rem 0 0;
    font-size: 0.8rem;
    color: var(--ent-muted);
    line-height: 1.5;
  }

  /* Tables */
  .payout-detail-page .po-table-wrap { box-shadow: none; }
  .payout-detail-page .token-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.125rem 0.5rem;
    background: var(--ent-neutral-bg);
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--ent-text);
  }
  .payout-detail-page .tx-hash-link {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.8125rem;
    color: var(--ent-blue);
    text-decoration: none;
  }
  .payout-detail-page .tx-hash-link:hover { text-decoration: underline; }
  .payout-detail-page .po-from-row {
    font-size: 0.8rem;
    color: var(--ent-text-2);
    padding-top: 0.75rem !important;
  }

  /* Empty state */
  .payout-detail-page .po-empty { text-align: center; padding: 1.5rem 1rem 0.5rem; }
  .payout-detail-page .po-empty-ic {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: var(--ent-neutral-bg);
    border: 1px solid var(--ent-border);
    display: inline-grid;
    place-items: center;
    color: var(--ent-muted);
    margin-bottom: 12px;
  }
  .payout-detail-page .po-empty h4 { margin: 0 0 5px; font-size: 0.9375rem; font-weight: 600; color: var(--ent-text); }
  .payout-detail-page .po-empty p { margin: 0 auto; font-size: 0.85rem; color: var(--ent-text-2); max-width: 380px; line-height: 1.5; }

  /* Activity */
  .payout-detail-page .activity-list { display: flex; flex-direction: column; gap: 0.875rem; }
  .payout-detail-page .activity-item { display: flex; align-items: flex-start; gap: 0.75rem; }
  .payout-detail-page .activity-icon-wrapper {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: var(--ent-neutral-bg);
  }
  .payout-detail-page .activity-icon-wrapper.success { background: var(--ent-green-bg); }
  .payout-detail-page .activity-icon-wrapper.primary { background: var(--ent-neutral-bg); }
  .payout-detail-page .activity-icon-wrapper.info { background: rgba(37, 99, 235, 0.12); }
  .payout-detail-page .activity-icon-wrapper.danger { background: var(--ent-red-bg); }
  .payout-detail-page .activity-icon-wrapper.warning { background: var(--ent-amber-bg); }
  .payout-detail-page .event-icon.success { color: var(--ent-green); }
  .payout-detail-page .event-icon.primary { color: var(--ent-text-2); }
  .payout-detail-page .event-icon.info { color: var(--ent-blue); }
  .payout-detail-page .event-icon.danger { color: var(--ent-red); }
  .payout-detail-page .event-icon.warning { color: var(--ent-amber); }
  .payout-detail-page .activity-content { display: flex; flex-direction: column; }
  .payout-detail-page .activity-message { color: var(--ent-text); font-size: 0.875rem; }
  .payout-detail-page .activity-date { color: var(--ent-muted); font-size: 0.8rem; }

  /* Reason cards */
  .payout-detail-page .payout-card p { margin: 0; font-size: 0.875rem; color: var(--ent-text-2); line-height: 1.5; }
  .payout-detail-page .notes-text { white-space: pre-wrap; }
  .payout-detail-page .rejection-card { border-color: var(--ent-red, #ef4444); background: var(--ent-red-bg, rgba(239,68,68,0.05)); }
  .payout-detail-page .rejection-card h3 { color: var(--ent-red, #ef4444); }
  .payout-detail-page .held-card { border-color: var(--ent-amber); background: var(--ent-amber-bg); }
  .payout-detail-page .held-card h3 { color: var(--ent-amber); }

  /* Reject modal */
  .payout-detail-page .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1001; }
  .payout-detail-page .action-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 440px;
    max-width: 92%;
    background: var(--ent-card);
    border: 1px solid var(--ent-border);
    border-radius: var(--ent-radius);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
    z-index: 1002;
  }
  .payout-detail-page .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid var(--ent-border);
  }
  .payout-detail-page .modal-header h3 { margin: 0; font-size: 1rem; font-weight: 600; color: var(--ent-text); }
  .payout-detail-page .close-btn {
    background: transparent;
    border: none;
    color: var(--ent-muted);
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 6px;
  }
  .payout-detail-page .close-btn:hover { background: var(--ent-card-alt); color: var(--ent-text); }
  .payout-detail-page .modal-content { padding: 1rem; }
  .payout-detail-page .form-group label {
    display: block;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--ent-text-2);
    margin-bottom: 0.5rem;
  }
  .payout-detail-page .form-group textarea {
    width: 100%;
    padding: 0.625rem;
    border: 1px solid var(--ent-border-strong);
    border-radius: 8px;
    font-size: 0.875rem;
    background: var(--ent-card-alt);
    color: var(--ent-text);
    resize: vertical;
    font-family: inherit;
  }
  .payout-detail-page .form-group textarea:focus { outline: none; border-color: var(--ent-primary); }
  .payout-detail-page .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem;
    border-top: 1px solid var(--ent-border);
  }

  /* Responsive */
  @media (max-width: 991px) {
    .payout-detail-page .payout-layout { flex-direction: column; }
  }
  @media (max-width: 640px) {
    .payout-detail-page .summary-grid { flex-direction: column; gap: 1.5rem; }
    .payout-detail-page .po-overview { flex-direction: column; gap: 1.25rem; }
    .payout-detail-page .po-overview-amount { border-right: none; border-bottom: 1px solid var(--ent-border, #e5e7eb); padding-right: 0; padding-bottom: 1.25rem; }
    .payout-detail-page .po-details-grid { grid-template-columns: 1fr; }
    .payout-detail-page .po-totals { max-width: 100%; }
    .payout-detail-page .po-actions .ent-btn { flex: 1 1 100%; }
  }
`;

export default PayoutDetail;
